import { useMemo } from 'react';
import {
  useGetClosingPackageCompositionQuery,
  useGetClosingPackageProjectionQuery,
} from '../api/closingPackageApi';
import { useGetApplicationByIdQuery } from '../../../store/api/applicationsApi';
import { buildClosingPackageTaskModalContextView } from '../lib/taskModalContext';

export type UseClosingPackageTaskModalDataOptions = {
  applicationId: number;
  open: boolean;
  loadComposition?: boolean;
};

export function useClosingPackageTaskModalData({
  applicationId,
  open,
  loadComposition = false,
}: UseClosingPackageTaskModalDataOptions) {
  const {
    data: application,
    isLoading: applicationLoading,
    isFetching: applicationFetching,
  } = useGetApplicationByIdQuery(applicationId, {
    skip: !open || !applicationId,
  });

  const {
    data: projection,
    isLoading: projectionLoading,
    isFetching: projectionFetching,
  } = useGetClosingPackageProjectionQuery(applicationId, {
    skip: !open || !applicationId,
  });

  const {
    data: composition,
    isLoading: compositionLoading,
    isFetching: compositionFetching,
  } = useGetClosingPackageCompositionQuery(applicationId, {
    skip: !open || !applicationId || !loadComposition,
  });

  const contextView = useMemo(
    () =>
      buildClosingPackageTaskModalContextView({
        applicationId,
        application,
        projection,
      }),
    [application, applicationId, projection]
  );

  const contextLoading =
    applicationLoading ||
    projectionLoading ||
    applicationFetching ||
    projectionFetching ||
    (loadComposition && (compositionLoading || compositionFetching));

  return {
    application,
    projection,
    composition,
    contextView,
    contextLoading,
  };
}
